// Category utility functions and mappings

export const CATEGORY_STRUCTURE = {
  'Fiction': {
    slug: 'fiction',
    subcategories: [
      { name: 'General Fiction', slug: 'general-fiction', dbValue: 'General Fiction' },
      { name: 'Romance & Drama', slug: 'romance-drama', dbValue: 'Romance & Drama' },
      { name: 'Fantasy/Thriller', slug: 'fantasy-thriller', dbValue: 'Fantasy/Thriller' },
      { name: 'Poetry', slug: 'poetry', dbValue: 'Poetry' },
      { name: 'Comics', slug: 'comics', dbValue: 'Comics' },
      { name: 'Rare/Collectors', slug: 'rare-collectors-fiction', dbValue: 'Rare/Collectors (Fiction)' },
    ]
  },
  'Non-Fiction': {
    slug: 'non-fiction',
    subcategories: [
      { name: 'Biographies & Memoirs', slug: 'biographies-memoirs', dbValue: 'Biographies & Memoirs' },
      { name: 'Self-Help & Personal Growth', slug: 'self-help-growth', dbValue: 'Self-Help & Personal Growth' },
      { name: 'History & Politics', slug: 'history-politics', dbValue: 'History & Politics' },
      { name: 'Science, Business & Lifestyle', slug: 'science-business-lifestyle', dbValue: 'Science, Business & Lifestyle' },
      { name: 'Rare/Collectors', slug: 'rare-collectors-nonfiction', dbValue: 'Rare/Collectors (Non-Fiction)' },
    ]
  },
  'Kids': {
    slug: 'children-young-readers',
    subcategories: [
      { name: 'Picture Books (0–8)', slug: 'picture-books', dbValue: 'Picture Books (0–8)' },
      { name: 'Middle Grade (8–12)', slug: 'middle-grade', dbValue: 'Middle Grade (8–12)' },
      { name: 'Young Adult (YA)', slug: 'young-adult', dbValue: 'Young Adult (YA)' },
      { name: 'Rare/Collectors', slug: 'rare-collectors-children', dbValue: 'Rare/Collectors (Children)' },
    ]
  },
  'Academic': {
    slug: 'academic-reference',
    subcategories: [
      { name: 'School & College Textbooks', slug: 'textbooks', dbValue: 'School & College Textbooks' },
      { name: 'Exam Prep & Study Guides', slug: 'exam-prep', dbValue: 'Exam Prep & Study Guides' },
      { name: 'Reference', slug: 'reference', dbValue: 'Reference' },
      { name: 'Rare/Collectors', slug: 'rare-collectors-academic', dbValue: 'Rare/Collectors (Academic)' },
    ]
  }
};

// Map URL slugs to database category values
export const getDbCategoryFromSlug = (slug: string): string | null => {
  // Check main categories
  for (const [categoryName, categoryData] of Object.entries(CATEGORY_STRUCTURE)) {
    if (categoryData.slug === slug) {
      return categoryName;
    }
    
    // Check subcategories
    const subcategory = categoryData.subcategories.find(sub => sub.slug === slug);
    if (subcategory) {
      return subcategory.dbValue;
    }
  }
  
  // Legacy category mappings
  const legacyMappings: { [key: string]: string } = {
    'textbook': 'School & College Textbooks',
    'kids-collections': 'Picture Books (0–8)',
    'academic': 'Academic & Reference'
  };
  
  return legacyMappings[slug] || null;
};

// Get display name from slug
export const getDisplayNameFromSlug = (slug: string): string => {
  const dbCategory = getDbCategoryFromSlug(slug);
  if (dbCategory) {
    return dbCategory;
  }
  
  // Fallback to formatted slug
  return slug.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Get category description
export const getCategoryDescription = (slug: string): string => {
  const descriptions: { [key: string]: string } = {
    'fiction': 'Discover captivating stories, novels, and literary works from various genres.',
    'general-fiction': 'Explore classic and contemporary fiction from all genres.',
    'romance-drama': 'Find heartwarming romance novels and compelling drama stories.',
    'fantasy-thriller': 'Dive into magical worlds and thrilling adventures.',
    'poetry': 'Discover beautiful poetry collections and verse.',
    'comics': 'Explore graphic novels, comics, and illustrated stories.',
    'non-fiction': 'Explore biographies, self-help, history, science, and factual books.',
    'biographies-memoirs': 'Read inspiring life stories and personal memoirs.',
    'self-help-growth': 'Books for personal development and self-improvement.',
    'history-politics': 'Understand the past and present through historical and political works.',
    'science-business-lifestyle': 'Explore science, business strategies, and lifestyle guides.',
    'children-young-readers': 'Books perfect for young minds and growing readers.',
    'picture-books': 'Beautifully illustrated books for children ages 0-8.',
    'middle-grade': 'Engaging stories for readers ages 8-12.',
    'young-adult': 'Compelling novels for teenage readers and young adults.',
    'academic-reference': 'Educational resources and reference materials.',
    'textbooks': 'School and college textbooks for academic study.',
    'exam-prep': 'Study guides and preparation materials for exams.',
    'reference': 'Dictionaries, encyclopedias, and reference materials.',
    'textbook': 'Find academic and educational books for students and professionals.',
    'kids-collections': 'Delightful children\'s books, picture books, and story collections for young readers.',
    'academic': 'Educational resources and reference materials for academic study.',
  };
  
  return descriptions[slug] || 'Browse our collection of quality second-hand books.';
};

// Get all subcategory db values for a main category
export const getSubcategoriesForMainCategory = (mainCategory: string): string[] => {
  const categoryData = CATEGORY_STRUCTURE[mainCategory as keyof typeof CATEGORY_STRUCTURE];
  if (categoryData) {
    return [mainCategory, ...categoryData.subcategories.map(sub => sub.dbValue)];
  }
  return [mainCategory];
};

// Check if a category name is a main category
export const isMainCategory = (categoryName: string): boolean => {
  return Object.keys(CATEGORY_STRUCTURE).includes(categoryName);
};

// Get all category slugs for routing
export const getAllCategorySlugs = (): string[] => {
  const slugs: string[] = [];
  
  Object.values(CATEGORY_STRUCTURE).forEach(category => {
    slugs.push(category.slug);
    category.subcategories.forEach(sub => {
      slugs.push(sub.slug);
    });
  });
  
  // Add legacy slugs
  slugs.push('textbook', 'kids-collections', 'academic');
  
  return slugs;
};